import type { VoidFunctionComponent } from 'react';
import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import { window } from '../../../../globals';
import { plural, pluralizeIssues } from '../../../../../shared/helpers';
import type { ResolutionReason, VulnIgnoreReason } from '../../../../../shared/issuesV2';
import {
  Category,
  ExceptionIgnoreScope,
  ExceptionPackageScope,
  IgnoredKind,
  IssueScopeKind,
  VulnIgnoreReasonDescriptions,
  VulnIgnoreReasons,
} from '../../../../../shared/issuesV2';
import { useToggle } from '../../../../hooks';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import type { SelectOption } from '../../../../types';
import { ignoreOrResolveWord, ignoreOrResolveWordActive } from '../../../../utils';
import {
  BUTTON_SIZE,
  BUTTON_TYPE,
  ButtonGroup,
  ErrorAlert,
  FeatureFlagRequired,
  FormGroup,
  InlineLoaderButton,
  LabeledTextArea,
  Select,
} from '../../../generic';
import Button from '../../../generic/Button';
import Flex from '../../../generic/Flex';
import InfoPanel from '../../../generic/InfoPanel';
import IgnoreReasonTitle from '../../../shared/vulns/IgnoreReasonTitle';
import useClearErrorsOnUnmount from '../hooks/useClearErrorsOnUnmount';
import IgnoreScopeOptions from './IgnoreScopeOptions';
import PackageScopeOptions from './PackageScopeOptions';
import ExpirationOptions from './ExpirationOptions';
import PackageLabelOptions from '../PackageLabelOptions';
import styles from './IgnoreWithExceptionsModal.module.less';
import { DISABLE_PACKAGE_LABELS } from '../../../../../shared/featureFlags/flagNames';
import PaidFeatureGate from '../../../generic/Gates/PaidFeatureGate';
import { isFreeTier } from '../../../../../shared/subscriptions';
import type { IgnoreModalOnIgnoreParams } from '../IgnoreIssuesModal';

type IgnoreIssuesModalBaseProps = {
  show: boolean;
  selectedIssuesCount?: number;
  onHide: () => void;
  isCategoryException: boolean;
};

type IgnoreIssuesModalProps = IgnoreIssuesModalBaseProps & {
  onIgnore: (ignoreProps: IgnoreModalOnIgnoreParams) => void;
};

/**
 * IgnoreIssuesModal represents the UI for the modal for ignoring issues.
 */
const IgnoreWithExceptionsModal: VoidFunctionComponent<IgnoreIssuesModalProps> = ({
  show,
  selectedIssuesCount,
  onHide,
  onIgnore,
  isCategoryException,
}) => {
  const scope = useSelector(IssuesV2Store.selectors.getScope);
  const issueCategory = useSelector(IssuesV2Store.selectors.getIssueCategory);

  const status = useSelector(IssuesV2Store.selectors.getAsyncStatusUpdateIssues);
  const { isLoading } = status;

  const [notes, setNotes] = useState<string>('');
  const [ignoreScope, setIgnoreScope] = useState<ExceptionIgnoreScope>(
    scope.type === 'releaseGroup' ? ExceptionIgnoreScope.ReleaseGroup : ExceptionIgnoreScope.Project,
  );
  const [packageScope, setPackageScope] = useState<ExceptionPackageScope>(ExceptionPackageScope.SelectedVersion);
  const [willIncludeReleaseGroups, onToggleAndReleaseGroups] = useToggle(false);
  const [resolutionReason, setResolutionReason] = useState<VulnIgnoreReason>(VulnIgnoreReasons.ComponentNotPresent);
  const [packageLabels, setPackageLabels] = useState<SelectOption[]>([]);
  const [arePackageLabelsEnabled, setArePackageLabelsEnabled] = useState(isCategoryException);

  const getIgnoreScope = () => {
    // Override the ignore scope if they're ignoring globally
    if (scope.type === IssueScopeKind.Global) {
      return ExceptionIgnoreScope.Organization;
    }
    // Override the ignore scope if they've selected "and release groups"
    if (ignoreScope === ExceptionIgnoreScope.Project && willIncludeReleaseGroups) {
      return ExceptionIgnoreScope.ProjectAndReleaseGroups;
    }
    return ignoreScope;
  };

  const [expirationDate, setExpirationDate] = useState<string | null>(null);

  const onIgnoreClick = () =>
    onIgnore({
      ignoredKind: IgnoredKind.IssueException,
      // Override the ignore scope if they've selected "and release groups"
      ignoreScope: getIgnoreScope(),
      ...(!isCategoryException && { packageScope }),
      notes,
      expiresAfter: expirationDate,
      resolutionReason: resolutionReason as ResolutionReason,
      packageLabelIds: packageLabels.map((label) => parseInt(label.value, 10)),
    });

  const onEnter = () => {
    setNotes('');
    setResolutionReason(VulnIgnoreReasons.ComponentNotPresent);
  };

  //  Ensure that when the component unmounts, we remove any error messages
  useClearErrorsOnUnmount();

  const isOrgFreeTier = isFreeTier({
    plan: window.user?.organization?.stripe_plan,
    stripePriceLimit: window.user?.organization?.stripePriceLimit,
  });

  const titleText = arePackageLabelsEnabled
    ? `Ignore ${issueCategory} issues`
    : `Ignore ${pluralizeIssues(selectedIssuesCount ?? 0)}`;

  const bodyText = arePackageLabelsEnabled
    ? `An issue ignore rule will be created for ${issueCategory} issues matching the selected conditions.`
    : `${(selectedIssuesCount ?? 0).toLocaleString()} ${pluralizeIssues(selectedIssuesCount ?? 0)} will be ignored.`;

  // If this is a category exception, we need to ensure that the user has enabled and selected at least one package label
  const isSubmitDisabled =
    isLoading ||
    (isCategoryException && (!arePackageLabelsEnabled || packageLabels.length === 0));

  const isVulnerability = issueCategory === Category.Vulnerability;
  const isQuality = issueCategory === Category.Quality;
  const hasExpirationDate = expirationDate !== null;

  // scope options should always be shown for non-category exceptions, and only hidden for global-scoped category exceptions
  const shouldShowScopeOptions = !(isCategoryException && scope.type === IssueScopeKind.Global);

  return (
    <Modal
      backdrop="static"
      animation
      show={show}
      onEnter={onEnter}
      onHide={onHide}
      onEscapeKeyDown={onHide}
      className="issues-modal"
    >
      <Modal.Header bsClass="issues-modal__header">
        <Modal.Title bsClass="issues-modal__header__title">{titleText}</Modal.Title>
      </Modal.Header>
      <Modal.Body bsClass="issues-modal__body">
        <Flex direction="column" justify="flex-start" align="flex-start">
          <span className="issues-modal__body__message">{bodyText}</span>
          {shouldShowScopeOptions && (
            <IgnoreScopeOptions
              setIgnoreScope={setIgnoreScope}
              ignoreScope={ignoreScope}
              willIncludeReleaseGroups={willIncludeReleaseGroups}
              issueCategory={issueCategory}
              scope={scope}
              onToggleAndReleaseGroups={onToggleAndReleaseGroups}
              isCategoryException={isCategoryException}
            />
          )}
          <div className={styles.conditions}>
            {isCategoryException && (
              <PaidFeatureGate isRestricted={isOrgFreeTier}>
                <FeatureFlagRequired flagName={DISABLE_PACKAGE_LABELS} isNotEnabled>
                  <FormGroup legend="When should it be ignored?" spacing="xs">
                    <PackageLabelOptions
                      setIsEnabled={setArePackageLabelsEnabled}
                      packageLabels={packageLabels}
                      setPackageLabels={setPackageLabels}
                    />
                  </FormGroup>
                </FeatureFlagRequired>
              </PaidFeatureGate>
            )}
            {!isCategoryException && !isQuality && (
              <PackageScopeOptions packageScope={packageScope} setPackageScope={setPackageScope} />
            )}
          </div>
          {isVulnerability && (
            <FormGroup legend={<IgnoreReasonTitle title="Reason for ignoring?" />} spacing="sm">
              <Select
                id="ignore-reason-dropdown"
                placeholder="Select an ignore reason (Optional)"
                value={{ value: resolutionReason, label: VulnIgnoreReasonDescriptions[resolutionReason] }}
                options={Object.entries(VulnIgnoreReasonDescriptions).map(([ignoreReason, label]) => ({
                  value: ignoreReason as VulnIgnoreReason,
                  label,
                }))}
                onChange={(o: SelectOption | null) => {
                  if (o) {
                    setResolutionReason(o.value as VulnIgnoreReason);
                  }
                }}
              />
            </FormGroup>
          )}
          <ExpirationOptions setExpirationDate={setExpirationDate} />
          <div className="issues-modal__body__notes">
            <LabeledTextArea
              label={`Add a note describing why ${plural(selectedIssuesCount ?? 0, 'this is', 'these are')} ignored:`}
              onChange={setNotes}
              isDisabled={isLoading}
              value={notes}
              placeholder="Type your note..."
            />
          </div>
        </Flex>
        {status.errors && (
          <div className="issues-modal__body__errors">
            <ErrorAlert status={status} />
          </div>
        )}
        {/* Only show this if they're trying to ignore for all versions or for a scope outside of the immediate project/RG */}
        {(packageScope !== ExceptionPackageScope.SelectedVersion ||
          (ignoreScope !== ExceptionIgnoreScope.Project && ignoreScope !== ExceptionIgnoreScope.ReleaseGroup) ||
          willIncludeReleaseGroups ||
          arePackageLabelsEnabled ||
          isCategoryException ||
          hasExpirationDate) && (
          <InfoPanel>
            <div className="fossa-font-size-sm">
              This action will create the {ignoreOrResolveWord().toLowerCase()} rule based on your selections and will
              take a few moments to process.
            </div>
          </InfoPanel>
        )}
      </Modal.Body>
      <Modal.Footer bsClass="issues-modal__footer">
        <ButtonGroup>
          <Button type={BUTTON_TYPE.SECONDARY} size={BUTTON_SIZE.MEDIUM} isDisabled={isLoading} onClick={onHide}>
            Cancel
          </Button>
          <InlineLoaderButton
            type={BUTTON_TYPE.PRIMARY}
            size={BUTTON_SIZE.MEDIUM}
            isSubmitButton
            isLoading={isLoading}
            isDisabled={isSubmitDisabled}
            onClick={onIgnoreClick}
          >
            {isLoading ? `${ignoreOrResolveWordActive()} issues...` : 'Ignore'}
          </InlineLoaderButton>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default IgnoreWithExceptionsModal;
